<template>
    <div>
        <template v-if="loading">
            <i class="fas fa-cog fa-spin"></i> Loading...
        </template>
        <template v-else>
            <vue-headful :title="agent.first_name + ' '+agent.last_name+' [Agent] - AMOS365'" />

            <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link to="/agents">Agents</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page">{{ agent.first_name }} {{ agent.last_name }}</li>
                </ol>
            </nav>

            <h4 class="page-header mb-4">
                {{ agent.first_name }} {{ agent.last_name }}
            </h4>

            <div class="mb-3" v-if="canEdit">
                <router-link :to="`/agents/edit/${agent.id}`" class="btn btn-sm btn-default"><i class="fas fa-edit"></i> Edit</router-link>
                <router-link :to="`/contracts/create?seller_type=Agent&seller_id=${agent.id}`" class="btn btn-sm btn-default" title="Assign Product(s)">
                    <i class="fa fa-plus-circle"></i> Assign Product
                </router-link>
            </div>

            <div class="row mt-2 mb-4">
                <div class="col-md-8 col-12">

                    <div class="card mb-4">
                        <div class="card-header"><strong>Details</strong></div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-6 mb-md-0">
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">First Name:</div>
                                        <div class="col-md-7">{{ agent.first_name }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Last Name:</div>
                                        <div class="col-md-7">{{ agent.last_name }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Middle Name:</div>
                                        <div class="col-md-7">{{ agent.middle_name }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Gender:</div>
                                        <div class="col-md-7">{{ agent.gender }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Suffix:</div>
                                        <div class="col-md-7">{{ agent.suffix }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Nickname:</div>
                                        <div class="col-md-7">{{ agent.nickname }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Last Updated At:</div>
                                        <div class="col-md-7">{{ agent.updated_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Last Updated By:</div>
                                        <div class="col-md-7">{{ agent.last_updated_by }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Marketer:</div>
                                        <div class="col-md-7">
                                            <router-link :to="`/agents/view/${agent.marketer_id}`" v-if="agent.marketer_id">
                                                {{ agent.marketer_name }}
                                            </router-link>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Date Marketer Assigned:</div>
                                        <div class="col-md-7">
                                            <span v-if="agent.marketer_date_assigned">{{ agent.marketer_date_assigned | moment("MM/DD/YYYY") }}</span>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Marketer Relationship Code:</div>
                                        <div class="col-md-7">
                                            <span v-if="agent.marketer_code_id">
                                                {{ agent.marketer_code_label }} <i class="fa fa-question-circle" :id="`marketer-rel-code-${agent.marketer_code_id}`"></i>
                                                <b-tooltip :target="`marketer-rel-code-${agent.marketer_code_id}`" triggers="hover" placement="right">
                                                    <span v-html="$sanitize(agent.marketer_code_desc)"></span>
                                                </b-tooltip>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Agency:</div>
                                        <div class="col-md-7">
                                            <router-link :to="`/agencies/view/${agent.agency_id}`" v-if="agent.agency_id">{{ agent.agency_name }}</router-link>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Master Level:</div>
                                        <div class="col-md-7">{{ agent.master_level }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Active:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-success" v-if="agent.is_active == 1">Active</label>
                                            <label class="badge badge-warning" v-else-if="agent.is_active == 0">Inactive</label>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Prospective:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-success" v-if="agent.is_prospect == 1">Yes</label>
                                            <label class="badge badge-default" v-else-if="agent.is_prospect == 0">No</label>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Can Contact:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-danger" v-if="agent.is_do_not_contact == 1">DO NOT CONTACT</label>
                                            <label class="badge badge-default" v-else-if="agent.is_do_not_contact == 0">Okay to Contact</label>

                                            <div v-if="agent.is_do_not_contact == 1 && agent.dnc_code_id">
                                                {{ agent.dnc_code_reason }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Can Contact Downline:</div>
                                        <div class="col-md-7">
                                            <label class="badge badge-danger" v-if="agent.is_do_not_contact_downline == 1">DO NOT CONTACT</label>
                                            <label class="badge badge-default" v-else-if="agent.is_do_not_contact_downline == 0">Okay to Contact</label>

                                            <div v-if="agent.is_do_not_contact_downline == 1 && agent.dnc_downline_code_id">
                                                {{ agent.dnc_downline_code_reason }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Products Managed:</div>
                                        <div class="col-md-7">
                                            {{ agent.num_products_managed }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header"><strong>Identification</strong></div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-6 mb-md-0">
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Tax ID:</div>
                                        <div class="col-md-7">
                                            <span v-if="$root.hasPermission('agents.view.sensitive')">{{ agent.tax_id | taxId }}</span>
                                            <span v-else>-- Hidden --</span>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">NPN:</div>
                                        <div class="col-md-7">{{ agent.npn }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">SSN:</div>
                                        <div class="col-md-7">
                                            <span v-if="$root.hasPermission('agents.view.sensitive')">{{ agent.ssn | ssn }}</span>
                                            <span v-else>-- Hidden --</span>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Date of Birth:</div>
                                        <div class="col-md-7">
                                            <span v-if="$root.hasPermission('agents.view.sensitive')">
                                                <span v-if="agent.date_of_birth">{{ agent.date_of_birth | moment("MM/DD/YYYY") }}</span>
                                            </span>
                                            <span v-else>-- Hidden --</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Res License#:</div>
                                        <div class="col-md-7">{{ agent.res_license_nbr }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">Res License State:</div>
                                        <div class="col-md-7">{{ agent.res_state_name }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">EO Expiration:</div>
                                        <div class="col-md-7">{{ agent.eo_expiration }}</div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col-md-4 text-md-right text-muted">AHIP:</div>
                                        <div class="col-md-7">{{ agent.ahip }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Management</strong>
                        </div>
                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-md-4 text-md-right text-muted">
                                    Direct Upline Manager:
                                </div>
                                <div class="col-md-7">
                                    <router-link :to="`/agents/view/${agent.direct_manager_id}`" v-if="agent.direct_manager && agent.direct_manager_type === 'Agent'">
                                        {{ agent.direct_manager.first_name }} {{ agent.direct_manager.last_name }}
                                    </router-link>
                                    <router-link :to="`/agencies/view/${agent.direct_manager_id}`" v-else-if="agent.direct_manager && agent.direct_manager_type === 'Agency'">
                                        {{ agent.direct_manager.agency_name }}
                                    </router-link>
                                    <span v-if="agent.direct_manager_type">({{ agent.direct_manager_type }})</span>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-4 text-md-right text-muted">
                                    Affiliation Manager:
                                </div>
                                <div class="col-md-7">
                                    <router-link :to="`/agents/view/${agent.top_manager_id}`" v-if="agent.top_manager && agent.top_manager_type === 'Agent'">
                                        {{ agent.top_manager.first_name }} {{ agent.top_manager.last_name }}
                                    </router-link>
                                    <router-link :to="`/agencies/view/${agent.top_manager_id}`" v-else-if="agent.top_manager && agent.top_manager_type === 'Agency'">
                                        {{ agent.top_manager.agency_name }}
                                    </router-link>
                                    <span v-if="agent.top_manager_type">({{ agent.top_manager_type }})</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header"><strong>Notes</strong></div>
                        <div class="card-body">
                            <div v-if="agent.notes">{{ agent.notes }}</div>
                            <div v-else>--</div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 col-12">
                    <div class="card mb-4">
                        <div class="card-header"><strong>Emails ({{ agent.emails.length }})</strong></div>
                        <div class="card-body">
                            <email-list :emails="agent.emails" />
                        </div>
                    </div>
                    <div class="card mb-4">
                        <div class="card-header"><strong>Phones ({{ agent.phones.length }})</strong></div>
                        <div class="card-body">
                            <phone-list :phones="agent.phones" />
                        </div>
                    </div>
                    <div class="card mb-4">
                        <div class="card-header"><strong>Addresses ({{ agent.addresses.length }})</strong></div>
                        <div class="card-body">
                            <address-list :addresses="agent.addresses" />
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header"><strong>Files ({{ files.length }})</strong></div>
                        <div class="card-body">
                            <files-list :files="files" />
                        </div>
                    </div>
                </div>
            </div>

            <ul class="search-nav nav nav-tabs tabs-alt container-m--x mt-4 d-lg-flex d-none">
                <b-nav-item :active="curTab === item.key" @click="curTab = item.key" v-for="item in tabs" :key="item.key">
                    <i :class="`fa fa-${item.icon}`"></i> {{ item.title }}
                </b-nav-item>
            </ul>

            <div class="mb-2 mb-lg-0 d-lg-none d-block">
                <b-dropdown :html="tabTitle" variant="default" class="bg-white">
                    <b-dropdown-item :active="curTab === item.key" @click="curTab = item.key" v-for="item in tabs" :key="item.key">
                        <i :class="`fa fa-${item.icon}`"></i> {{ item.title }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>

            <div class="pt-2">
                <div class="card" v-show="curTab === 'assigned'">
                    <div class="card-body">
                        <products-assigned assetType="Agent" :sellerId="agent.id" sellerType="Agent" :products="products_assigned" :emails="agent.emails" />
                    </div>
                </div>

                <div class="card" v-show="curTab === 'managed'">
                    <div class="card-body">
                        <products-managed assetType="Agent" :managerId="agent.id" managerType="Agent" :products="products_managed" />
                    </div>
                </div>

                <div v-show="curTab === 'comments'">
                    <template v-if="$root.hasPermission('comments.create')">
                        <comments-form ref="agentCommentForm" assetType="Agent" :assetId="agent.id" @commentCreated="newComment" @commentUpdated="updateComment"></comments-form>
                        <hr/>
                    </template>

                    <comments-list ref="agentComments" assetType="Agent" :assetId="agent.id" :ajaxLoad="true" @editComment="editComment" @commentCount="setCommentCount"></comments-list>
                </div>
                <div class="card" v-show="curTab === 'files'">
                    <div class="card-body">
                        <files-list :files="files" />
                    </div>
                </div>
                <div v-show="curTab === 'history'">
                    <div class="card">
                        <div class="card-body">
                            <history-list :record_id="agent.id" record_type="Agent" @historyCount="(count) => { this.historyCount = count; }" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

    </div>
</template>

<script>
    export default {
        data() {
            return {
                loading: true,
                curTab: 'assigned',
                id: this.$route.params.id,
                userTimezone: this.$root.timezone,
                agent: {},
                parts: [],
                comments: [],
                all_comments: [],
                products_assigned: [],
                products_managed: [],
                files: [],
                assignedCount: 0,
                managedCount: 0,
                totalComments: 0,
                historyCount: 0,
            }
        },

        computed: {
            canEdit(){
                //Check Permissions
                return this.$root.hasPermission('agents.manage');
            },

            tabs(){
                return [
                    {key: 'assigned', title: 'Products Assigned ('+this.products_assigned.length+')', icon: 'book'},
                    {key: 'managed', title: 'Products Managed ('+this.products_managed.length+')', icon: 'sitemap'},
                    {key: 'comments', title: 'Comments ('+this.totalComments+')', icon: 'comments'},
                    {key: 'files', title: 'Files ('+this.files.length+')', icon: 'clone'},
                    {key: 'history', title: 'History ('+this.historyCount+')', icon: 'clock'},
                ];
            },

            tabTitle(){
                //Loop
                for (var i = this.tabs.length - 1; i >= 0; i--)
                {
                    //Check tabl
                    if(this.tabs[i].key === this.curTab)
                    {
                        return '<i class="fa fa-'+this.tabs[i].icon+'"></i> ' + this.tabs[i].title;
                    }
                }

                //Send it
                return '';
            },
        },

        methods: {
            //Get the Agent
            getAgent(){
                //Send it
                this.$http.get('/agents/view/'+this.id)
                    .then(response => {
                        //Set the agent
                        this.agent = response.data.agent;
                        this.products_assigned = response.data.products_assigned;
                        this.products_managed = response.data.products_managed;
                        this.files = response.data.files;
                    })
                    .catch(error => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            setCount(data){
                //Setter
                this.assignedCount = data;
            },

            editComment(comment){
                //Push into the component
                this.$refs.agentCommentForm.editComment(comment);
            },

            newComment(comment){
                //Push it
                this.$refs.agentComments.addComment(comment);
            },

            updateComment(comment){
                //Update it
                this.$refs.agentComments.updateComment(comment);
            },

            setCommentCount(count){
                //SEt it
                this.totalComments = count;
            },
        },

        mounted() {
            //Get the agent
            this.getAgent();
        }
    }
</script>
